/**
 * Universal-ID collection — best-effort pickup of identity tokens that
 * publishers have already integrated via their own SDKs. We never *issue*
 * an identity; we just relay what's already on the page so downstream
 * bidders can match without a third-party cookie.
 *
 * Sources scanned, in order of preference:
 *   - UID 2.0    (The Trade Desk)    — window.__uid2.advertising_token
 *                                    + localStorage["__uid2_advertising_token"]
 *   - ID5        (id5.io)            — window.ID5.userId() result.universal_uid
 *                                    + localStorage["id5id"]
 *   - LiveRamp   (ATS)               — window.LiveRampATS?.envelope
 *
 * Output shape matches OpenRTB 2.6 §3.2.27 `EID`:
 *   { source: 'uidapi.com', uids: [{ id: '<token>', atype: 3 }] }
 *
 * `atype` codes (IAB OpenRTB User Agents and IDs):
 *   1 = Authenticated device, persistent.
 *   2 = Authenticated device, transient (browser session).
 *   3 = Browser cookie ID / first-party identifier.
 */

export interface Eid {
  /** Canonical source domain per IAB UID registry. */
  source: string;
  uids: Array<{ id: string; atype: number; ext?: Record<string, unknown> }>;
}

interface Uid2Window {
  __uid2?: { advertising_token?: string; getAdvertisingToken?: () => string };
}
interface Id5Window {
  ID5?: { userId?: () => { universal_uid?: string } | undefined };
}
interface LiveRampWindow {
  LiveRampATS?: { envelope?: string };
}

type AnyWindow = Window & Uid2Window & Id5Window & LiveRampWindow;

const LS_KEYS = {
  uid2: '__uid2_advertising_token',
  id5: 'id5id',
} as const;

function readLocalStorage(key: string): string | undefined {
  try {
    const v = localStorage.getItem(key);
    if (!v) return undefined;
    try {
      const parsed = JSON.parse(v) as Record<string, unknown>;
      if (parsed && typeof parsed === 'object') {
        const candidate =
          (parsed['advertising_token'] as string | undefined) ??
          (parsed['universal_uid'] as string | undefined) ??
          (parsed['token'] as string | undefined);
        return typeof candidate === 'string' && candidate.length > 0 ? candidate : undefined;
      }
    } catch {
      // not JSON — fall through to raw string
    }
    return v;
  } catch {
    // localStorage can throw in cross-origin / privacy-mode browsers.
    return undefined;
  }
}

function collectUid2(w: AnyWindow): Eid | null {
  let token: string | undefined;
  const winUid = w.__uid2;
  if (winUid?.advertising_token) token = winUid.advertising_token;
  if (!token && typeof winUid?.getAdvertisingToken === 'function') {
    try { token = winUid.getAdvertisingToken(); } catch { /* swallow */ }
  }
  if (!token) token = readLocalStorage(LS_KEYS.uid2);
  if (!token) return null;
  return { source: 'uidapi.com', uids: [{ id: token, atype: 3 }] };
}

function collectId5(w: AnyWindow): Eid | null {
  let token: string | undefined;
  const id5 = w.ID5;
  if (typeof id5?.userId === 'function') {
    try { token = id5.userId()?.universal_uid; } catch { /* swallow */ }
  }
  if (!token) token = readLocalStorage(LS_KEYS.id5);
  if (!token) return null;
  return { source: 'id5-sync.com', uids: [{ id: token, atype: 1 }] };
}

function collectLiveRamp(w: AnyWindow): Eid | null {
  const envelope = w.LiveRampATS?.envelope;
  if (!envelope) return null;
  return { source: 'liveramp.com', uids: [{ id: envelope, atype: 3 }] };
}

/**
 * Returns the set of identity tokens currently available on the page.
 * Safe to call in non-browser environments (returns []).
 */
export function collectEids(): Eid[] {
  if (typeof window === 'undefined') return [];
  const w = window as AnyWindow;
  const out: Eid[] = [];
  const uid2 = collectUid2(w);
  if (uid2) out.push(uid2);
  const id5 = collectId5(w);
  if (id5) out.push(id5);
  const lr = collectLiveRamp(w);
  if (lr) out.push(lr);
  return out;
}
